/**
 * DataMoonPage.tsx — Tenant-facing DataMoon prospect sourcing UI.
 *
 * Mounted at a route accessible to MANAGER and ADMIN roles.
 * Shows:
 *   1. Status card — quota used / remaining, last pull date.
 *   2. Fetch form — optional filters (field/operator/value rows) + limit.
 *   3. Result summary after a successful pull.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { datamoonApi } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface DataMoonStatus {
  enabled: boolean;
  quota_used: number | null;
  prospect_quota: number | null;
  quota_reset_at: string | null;
  last_pull_at: string | null;
}

interface FetchResult {
  audience_id: number;
  rows_fetched: number;
  imported: number;
  skipped_duplicate: number;
  skipped_no_name: number;
}

interface FilterRow {
  id: string;
  field: string;
  operator: string;
  value: string;
}

const OPERATOR_OPTIONS = [
  { label: "equals", value: "eq" },
  { label: "not equals", value: "neq" },
  { label: "in list", value: "in" },
  { label: "not in list", value: "nin" },
  { label: "contains", value: "contains" },
  { label: "greater than", value: "gt" },
  { label: "less than", value: "lt" },
];

function newFilter(): FilterRow {
  return { id: crypto.randomUUID(), field: "", operator: "eq", value: "" };
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function DataMoonPage() {
  const qc = useQueryClient();

  // ── Status query ───────────────────────────────────────────────────────────
  const statusQuery = useQuery<DataMoonStatus>({
    queryKey: ["datamoon-status"],
    queryFn: () => datamoonApi.status(),
    refetchInterval: false,
  });

  // ── Form state ─────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [limit, setLimit] = useState("500");
  const [lastResult, setLastResult] = useState<FetchResult | null>(null);

  function addFilter() {
    setFilters((prev) => [...prev, newFilter()]);
  }

  function removeFilter(id: string) {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  }

  function updateFilter(id: string, key: keyof FilterRow, val: string) {
    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: val } : f))
    );
  }

  // ── Fetch mutation ─────────────────────────────────────────────────────────
  const fetchMut = useMutation({
    mutationFn: () => {
      const validFilters = filters
        .filter((f) => f.field.trim() && f.value.trim())
        .map((f) => ({
          field: f.field.trim(),
          operator: f.operator,
          // "in" / "nin" operators expect an array — split on comma
          value:
            f.operator === "in" || f.operator === "nin"
              ? f.value.split(",").map((v) => v.trim())
              : f.value.trim(),
        }));

      return datamoonApi.fetchProspects({
        filters: validFilters,
        limit: Number(limit) || 500,
      });
    },
    onSuccess: (data) => {
      setLastResult(data);
      toast.success(`Imported ${data.imported} new prospects from DataMoon.`);
      void qc.invalidateQueries({ queryKey: ["datamoon-status"] });
    },
    onError: (e: unknown) => {
      const msg = (e as { detail?: string; message?: string }).detail
        ?? (e as Error).message
        ?? "Unknown error";
      toast.error(`DataMoon pull failed: ${msg}`);
    },
  });

  const status = statusQuery.data;
  const quotaRemaining =
    status?.prospect_quota != null && status?.quota_used != null
      ? status.prospect_quota - status.quota_used
      : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">DataMoon Sourcing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pull verified B2B prospects directly into your prospect table.
        </p>
      </div>

      {/* ── Status card ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4" />
            Integration Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : statusQuery.isError ? (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Failed to load DataMoon status. Check your connection.
            </div>
          ) : !status?.enabled ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              DataMoon is not enabled for your account. Contact your platform
              administrator.
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <Badge variant="default" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Quota Used</dt>
                <dd className="font-medium">
                  {status.quota_used?.toLocaleString() ?? "—"}
                  {status.prospect_quota != null && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      / {status.prospect_quota.toLocaleString()}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Remaining</dt>
                <dd
                  className={
                    quotaRemaining != null && quotaRemaining < 50
                      ? "font-medium text-destructive"
                      : "font-medium text-green-600"
                  }
                >
                  {quotaRemaining?.toLocaleString() ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Quota Resets</dt>
                <dd className="text-muted-foreground">
                  {status.quota_reset_at
                    ? formatDateTime(status.quota_reset_at)
                    : "—"}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-4">
                <dt className="text-muted-foreground">Last Pull</dt>
                <dd className="text-muted-foreground">
                  {status.last_pull_at
                    ? formatDateTime(status.last_pull_at)
                    : "Never"}
                </dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      {/* Only show the fetch form when enabled */}
      {status?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Download className="h-4 w-4" />
              Fetch Prospects
            </CardTitle>
            <CardDescription>
              Add optional filters to narrow the audience. Leave filters empty to
              pull the broadest available set (subject to your quota).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                fetchMut.mutate();
              }}
              className="space-y-5"
            >
              {/* Filters */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Audience Filters</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addFilter}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add Filter
                  </Button>
                </div>

                {filters.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No filters — all available prospects will be returned (up to
                    your limit).
                  </p>
                )}

                {filters.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-start gap-2 rounded-md border p-3"
                  >
                    <div className="grid flex-1 grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Field
                        </Label>
                        <Input
                          placeholder="e.g. primary_industry"
                          value={f.field}
                          onChange={(e) =>
                            updateFilter(f.id, "field", e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Operator
                        </Label>
                        <select
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={f.operator}
                          onChange={(e) =>
                            updateFilter(f.id, "operator", e.target.value)
                          }
                        >
                          {OPERATOR_OPTIONS.map((op) => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Value{" "}
                          {(f.operator === "in" || f.operator === "nin") && (
                            <span className="text-muted-foreground/70">
                              (comma-separated)
                            </span>
                          )}
                        </Label>
                        <Input
                          placeholder={
                            f.operator === "in" || f.operator === "nin"
                              ? "Technology, SaaS"
                              : "value"
                          }
                          value={f.value}
                          onChange={(e) =>
                            updateFilter(f.id, "value", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="mt-6 rounded p-1 text-muted-foreground hover:text-destructive"
                      onClick={() => removeFilter(f.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Limit */}
              <div className="space-y-1.5">
                <Label htmlFor="dm-limit">
                  Max Records{" "}
                  <span className="text-xs text-muted-foreground">
                    (1–{Math.min(5000, quotaRemaining ?? 5000)}, capped by remaining
                    quota)
                  </span>
                </Label>
                <Input
                  id="dm-limit"
                  type="number"
                  min={1}
                  max={5000}
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  className="max-w-xs"
                />
              </div>

              <Button
                type="submit"
                disabled={fetchMut.isPending || quotaRemaining === 0}
                className="w-full sm:w-auto"
              >
                {fetchMut.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Fetching prospects…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Fetch Prospects
                  </>
                )}
              </Button>

              {quotaRemaining === 0 && (
                <p className="text-sm text-destructive">
                  Your monthly quota is exhausted. It resets on{" "}
                  {status.quota_reset_at
                    ? formatDateTime(status.quota_reset_at)
                    : "your next billing cycle"}
                  .
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {/* ── Result card ── */}
      {lastResult && (
        <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-green-800 dark:text-green-300">
              <CheckCircle2 className="h-4 w-4" />
              Pull Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">Audience ID</dt>
                <dd className="font-mono font-medium">{lastResult.audience_id}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Rows Fetched</dt>
                <dd className="font-medium">{lastResult.rows_fetched.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Imported</dt>
                <dd className="font-medium text-green-700 dark:text-green-400">
                  {lastResult.imported.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Duplicates Skipped</dt>
                <dd className="font-medium">{lastResult.skipped_duplicate.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">No-Name Skipped</dt>
                <dd className="font-medium">{lastResult.skipped_no_name.toLocaleString()}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
